import { createSelector } from 'reselect';

import { isPresent, prop } from '../../../../../helpers';
import { GITLAB_COMMIT_PL } from '../../../../constants';
import { RootActions, RootActionTypes } from '../../../../ngrx/actions/root.action';
import { insertReplaceState, removeOne } from '../../../../ngrx/functions/reducer';
import { ESInterface, getEmptyESState } from '../../../../ngrx/state';
import { createCommonSelectors, getByIds } from '../../../../util/store';
import { ESGitlabCommit, GitlabCommit } from '../../models/git-commit';
import * as gitlabCompany from '../gitlab-company/gitlab-company.actions';
import { createMapWithArray } from '../util';
import * as gitlabCommit from './gitlab-commit.actions';

export interface State extends ESGitlabCommit {
  taskCommits: {
    [taskId: number]: number[];
  };
  isLoading: boolean;
}

const initialState: State = {
  ...getEmptyESState<GitlabCommit>(),
  taskCommits: {},
  isLoading: false
};

export function reducer(
  state = initialState,
  action: gitlabCommit.Actions | gitlabCompany.Actions | RootActions
): State {
  switch (action.type) {
    case gitlabCommit.GET_TASK_COMMIT: {
      return { ...state, isLoading: true };
    }
    case gitlabCommit.UPDATED: {
      return state;
    }

    case gitlabCommit.GET_TASK_COMMIT_COMPLETE: {
      const taskCommits = {
        ...state.taskCommits,
        [action.payload.taskId]: action.payload.entities.ids
      };
      return {
        ...state,
        taskCommits: taskCommits,
        isLoading: false
      };
    }

    case gitlabCommit.GET_TASK_COMMIT_FAIL: {
      return { ...state, isLoading: false };
    }

    case gitlabCommit.DELETE_TASK_COMMIT: {
      const newState = removeOne(state, action.payload.id);
      const taskCommits = newState.taskCommits[action.payload.taskId] || [];
      newState.taskCommits = {
        ...newState.taskCommits,
        [action.payload.taskId]: taskCommits.filter(cId => cId !== action.payload.id)
      };
      return newState;
    }

    case gitlabCompany.REMOVE_ALL_COMPLETE: {
      return initialState;
    }

    case RootActionTypes.GET_COMPLETE: {
      if (GITLAB_COMMIT_PL in action.payload) {
        const updatedState: ESInterface<GitlabCommit> = action.payload[GITLAB_COMMIT_PL];
        const taskCommits = updatedState.ids.reduce(
          createMapWithArray(updatedState.entities, 'task', 'id'),
          Object.assign({}, state.taskCommits)
        );
        const newESState = <State>insertReplaceState(state, updatedState);
        return {
          ...newESState,
          taskCommits,
          isLoading: false
        };
      }

      return state;
    }

    default: {
      return state;
    }
  }
}

export const { getState, getEntities, getIds, getAll, isEmpty } = createCommonSelectors<GitlabCommit, State>(
  GITLAB_COMMIT_PL
);

export const getTaskCommitMap = createSelector(getState, prop('taskCommits'));

export const getTaskCommitIds = createSelector(getTaskCommitMap, state => taskId => state[taskId] || []);
export const getTaskCommits = createSelector(
  getTaskCommitIds,
  getEntities,
  (commitIdsFn, entities) => (taskId: number) => getByIds(commitIdsFn(taskId), entities).filter(isPresent)
);

const isLoading = createSelector(getState, state => state.isLoading);

export const getTaskCommitsLoading = createSelector(isLoading, getTaskCommitIds, (isLoadingFlag, idsFn) => taskId =>
  isLoadingFlag && idsFn(taskId).length === 0
);
